// "Today" panel on the globe screen:
//   1. The world in 60 seconds: the day's 5 most important stories (GET /api/news/top),
//      at most one per country, so it reads like a quick world tour.
//   2. Central-bank decisions with live countdowns (features/calendar.js).
//   3. Your countries: the countries you follow, one tap to open them.
// It opens automatically on the first visit of each day, then stays out of the way.

import { upcomingDecisions, describe } from './calendar.js';
import { getFollows, onFollowsChange } from './follow.js';
import { explain } from './glossary.js';
import { shareStory } from './share.js';
import { t, countryName, catName } from '../i18n/index.js';

const SEEN_KEY = 'nnw:today-seen';
let root;
let toggleBtn;
let topStories = [];
let countriesByIso = new Map();
let openCountry = () => {};

const esc = (s) =>
    String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

function safeUrl(u) {
    try {
        const url = new URL(u);
        return url.protocol === 'http:' || url.protocol === 'https:' ? esc(url.href) : null;
    } catch {
        return null;
    }
}

function renderTop() {
    if (!topStories.length) {
        return `<p class="td-empty">${esc(t('today.emptyTop'))}</p>`;
    }
    return `<ol class="td-top">${topStories
        .map((a, i) => {
            const url = safeUrl(a.url);
            const ex = explain(a);
            const term = ex.terms[0];
            return `<li class="td-story" data-i="${i}">
                <button type="button" class="td-country" data-iso2="${esc(a.country_iso2)}" title="${esc(t('today.open', { country: countryName(a.country_iso2, a.country_name) }))}">
                    <span aria-hidden="true">${esc(a.flag_emoji || '🌍')}</span> ${esc(countryName(a.country_iso2, a.country_name))}
                </button>
                <span class="td-cat" style="color:${esc(a.category_color || '#ff8a3d')}">${esc(catName(a.category_slug, a.category_name || ''))}</span>
                <p class="td-title">${url ? `<a href="${url}" target="_blank" rel="noopener noreferrer">${esc(a.title)}</a>` : esc(a.title)}</p>
                ${term ? `<p class="td-why"><b>${esc(term.label)}:</b> ${esc(term.why)}</p>` : ''}
                <div class="td-actions">
                    <span class="td-src">${esc(a.source_name)}</span>
                    <button type="button" class="td-share" data-i="${i}">${esc(t('share'))}</button>
                </div>
            </li>`;
        })
        .join('')}</ol>`;
}

function renderBanks() {
    const now = Date.now();
    return `<ul class="td-banks">${upcomingDecisions(now)
        .map((b) => {
            const d = describe(b, now);
            return `<li><b>${esc(b.short)}</b><span class="td-bank-name">${esc(t('bank.' + b.id))}</span>
                <span class="td-bank-when">${esc(d.when)}</span><span class="td-bank-left">${esc(d.status)}</span></li>`;
        })
        .join('')}</ul>`;
}

function renderMine() {
    const { countries } = getFollows();
    if (!countries.length) {
        return `<p class="td-empty">${esc(t('today.emptyMine'))}</p>`;
    }
    return `<div class="td-mine">${countries
        .map((iso) => {
            const c = countriesByIso.get(iso);
            return `<button type="button" class="td-country td-chip" data-iso2="${esc(iso)}">${esc(c?.flag_emoji || '')} ${esc(countryName(iso, c?.name || iso))}</button>`;
        })
        .join('')}</div>`;
}

function render() {
    if (!root) return;
    root.innerHTML = `
        <div class="td-head">
            <h2>${esc(t('today'))}</h2>
            <button type="button" class="td-close" aria-label="${esc(t('close'))}">×</button>
        </div>
        <section><h3>${esc(t('today.top'))}</h3>${renderTop()}</section>
        <section><h3>${esc(t('today.banks'))}</h3>${renderBanks()}</section>
        <section><h3>${esc(t('today.mine'))}</h3>${renderMine()}</section>
        <p class="td-note">${esc(t('today.note'))}</p>`;

    root.querySelector('.td-close').addEventListener('click', () => setOpen(false));
    root.querySelectorAll('.td-country').forEach((b) =>
        b.addEventListener('click', () => {
            openCountry(b.dataset.iso2);
            if (window.innerWidth < 900) setOpen(false);
        }),
    );
    root.querySelectorAll('.td-share').forEach((b) =>
        b.addEventListener('click', () => {
            const a = topStories[Number(b.dataset.i)];
            shareStory({ ...a, country_name: countryName(a.country_iso2, a.country_name), category_name: catName(a.category_slug, a.category_name) });
        }),
    );
}

function setOpen(open) {
    root.classList.toggle('open', open);
    toggleBtn?.setAttribute('aria-expanded', String(open));
    if (open) {
        render();
        localStorage.setItem(SEEN_KEY, new Date().toDateString());
    }
}

async function loadTop() {
    try {
        const res = await fetch('/api/news/top?limit=5');
        const json = await res.json();
        topStories = Array.isArray(json.data) ? json.data : [];
    } catch {
        topStories = [];
    }
    if (root.classList.contains('open')) render();
}

/**
 * @param {object} opts
 * @param {(iso2: string) => void} opts.onOpenCountry   open a country on the globe
 * @param {Array} opts.countries                        country list (for names/flags)
 */
export function initToday({ onOpenCountry, countries = [] }) {
    openCountry = onOpenCountry;
    countriesByIso = new Map(countries.map((c) => [c.iso2, c]));

    const stage = document.querySelector('.globe-stage');
    if (!stage) return;
    root = document.createElement('aside');
    root.className = 'today-panel';
    root.setAttribute('aria-label', t('today'));
    stage.appendChild(root);

    toggleBtn = document.createElement('button');
    toggleBtn.type = 'button';
    toggleBtn.className = 'today-toggle';
    toggleBtn.setAttribute('aria-expanded', 'false');
    toggleBtn.innerHTML = `<span class="today-dot" aria-hidden="true"></span> ${esc(t('today'))}`;
    toggleBtn.addEventListener('click', () => setOpen(!root.classList.contains('open')));
    stage.appendChild(toggleBtn);

    onFollowsChange(() => root.classList.contains('open') && render());
    setInterval(() => root.classList.contains('open') && render(), 60_000); // countdowns

    loadTop();
    // First visit of the day: open it automatically (not on small phones).
    if (localStorage.getItem(SEEN_KEY) !== new Date().toDateString() && window.innerWidth >= 900) {
        setOpen(true);
    }
}

export function updateTodayCountries(countries) {
    countriesByIso = new Map(countries.map((c) => [c.iso2, c]));
}
